import React, { memo } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { composeStyle } from '@linqpal/common-frontend/src/helpers'
import BuilderBottomModal from '../../../../ui/molecules/BuilderBottomModal'
import {
  BtInput,
  BtNumberInput,
  BtRadioGroup,
  BtText,
  Spacer,
} from '@linqpal/components/src/ui'

export interface Form {
  jobId: string
  streetAddress: string
  unitNumber?: string
  city: string
  state: string
  zip: string
  projectType: 'commercial' | 'residential' | ''
  firstName: string
  lastName: string
}

interface AddNewProjectModalProps {
  initialValues?: Partial<Form>
  onCreate: (payload: Form) => void
  onClose: () => void
}

export const AddNewProjectModal: React.FC<AddNewProjectModalProps> = memo(
  ({ initialValues, onCreate, onClose }) => {
    const { t } = useTranslation('global')

    const {
      jobId = '',
      streetAddress = '',
      unitNumber = '',
      city = '',
      state = '',
      zip = '',
      projectType = '',
      firstName = '',
      lastName = '',
    } = initialValues || {}

    const {
      values,
      touched,
      errors,
      submitForm,
      setFieldValue,
      setFieldTouched,
      //setSubmitting,
      isSubmitting,
    } = useFormik<Form>({
      // eslint-disable-next-line prettier/prettier
      initialValues: {
        jobId,
        streetAddress,
        unitNumber,
        city,
        state,
        zip,
        projectType,
        firstName,
        lastName,
      },
      validateOnBlur: true,
      validationSchema: yup.object({
        jobId: yup.string().required('Required'),
        streetAddress: yup.string().required('Required'),
        unitNumber: yup.string(),
        city: yup.string().required('Required'),
        state: yup.string().required('Required'),
        zip: yup
          .string()
          .length(5, t('ProjectForm.invalid-zip'))
          .required('Required'),
        projectType: yup.string().required('Required'),
        firstName: yup.string().required('Required'),
        lastName: yup.string().required('Required'),
      }),
      onSubmit: (form) => {
        console.log('form submit')
        onCreate(form)
      },
    })

    console.log(errors)

    // const handleSelectStreetAddres = useCallback(
    //   (val) => {
    //     const { address, city, state, zip } = val
    //     setValues({ ...values, address, city, state, zip })
    //   },
    //   [setValues, values],
    // )

    return (
      <BuilderBottomModal
        visible={true}
        onClose={onClose}
        testID={'add-new-project-modal'}
      >
        <Text
          style={styles.title1}
          testID={'add-new-project-title-no-project-exist'}
        >
          {t('ProjectForm.main-heading-when-no-projects-exist')}
        </Text>
        <Text style={styles.subtitle} testID={'add-new-project-subtitle'}>
          {t('ProjectForm.main-subheading')}
        </Text>

        <Text style={styles.title2} testID={'add-new-project-title-job-id'}>
          {t('ProjectForm.what-is-jobid')}
        </Text>
        <BtInput
          size="medium"
          value={values.jobId}
          status={errors.jobId && touched.jobId ? 'danger' : ''}
          onChangeText={(value) => setFieldValue('jobId', value)}
          onBlur={() => setFieldTouched('jobId')}
          mandatory
          label={t('ProjectForm.jobid-label')}
          testID="jobId"
        />

        <Text style={styles.title2} testID={'add-new-project-address-title'}>
          {t('ProjectForm.address-title')}
        </Text>
        <Text
          style={styles.subtitle}
          testID={'add-new-project-address-subtitle'}
        >
          {t('ProjectForm.address-subtitle')}
        </Text>
        <BtInput
          size="medium"
          value={values.streetAddress}
          // onSelect={handleSelectStreetAddres}
          status={errors.streetAddress && touched.streetAddress ? 'danger' : ''}
          onChangeText={(value) => setFieldValue('streetAddress', value)}
          onBlur={() => setFieldTouched('streetAddress')}
          label={t('ProjectForm.street-address')}
          testID="streetAddress"
          mandatory
        />
        <BtNumberInput
          size="medium"
          value={values.unitNumber}
          onChangeText={(value) => setFieldValue('unitNumber', value)}
          style={{ marginTop: 20 }}
          label={t('ProjectForm.unit-num')}
          testID="unitNumber"
          format={'#####'}
        />
        <BtInput
          size="medium"
          mandatory
          value={values.state}
          status={errors.state && touched.state ? 'danger' : ''}
          onChangeText={(value) => setFieldValue('state', value)}
          onBlur={() => setFieldTouched('state')}
          label={t('ProjectForm.state')}
          testID={'state'}
          containerStyle={{ marginTop: 20 }}
        />
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            marginTop: 20,
          }}
        >
          <BtNumberInput
            size="medium"
            value={values.zip}
            onChangeText={(value) => setFieldValue('zip', value)}
            onBlur={() => setFieldTouched('zip')}
            style={composeStyle(
              { width: '48%' },
              errors.zip && touched.zip ? { borderColor: 'red' } : {},
            )}
            label={t('ProjectForm.zip')}
            testID="zip"
            validate={() => (errors.zip ? t('ProjectForm.invalid-zip') : '')}
            format={'#####'}
            required
          />
          <BtInput
            size="medium"
            value={values.city}
            status={errors.city && touched.city ? 'danger' : ''}
            onChangeText={(value) => setFieldValue('city', value)}
            onBlur={() => setFieldTouched('city')}
            style={{ width: '48%' }}
            label={t('ProjectForm.city')}
            testID="city"
            mandatory
          />
        </View>
        <Text style={styles.title2} testID={'add-new-project--type-title'}>
          {t('ProjectForm.project-type-title') + '*'}
        </Text>
        <BtRadioGroup
          value={values.projectType}
          onChange={(value) => {
            setFieldTouched('projectType')
            setFieldValue('projectType', value)
          }}
          options={[
            { label: t('ProjectForm.commercial'), value: 'commercial' },
            { label: t('ProjectForm.residential'), value: 'residential' },
          ]}
          radioStyle={{ marginRight: 32 }}
          groupStyle={{ flexDirection: 'row' }}
          labelStyle={{ color: '#19262F', fontWeight: '500', fontSize: 16 }}
          testID="projectType_privateType"
          error={
            errors.projectType && touched.projectType ? errors.projectType : ''
          }
        />
        <Text
          style={styles.title2}
          testID={'add-new-project-home-owner-info-title'}
        >
          {t('ProjectForm.enter-home-owner-information')}
        </Text>
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
            marginTop: 20,
          }}
        >
          <BtInput
            size="medium"
            value={values.firstName}
            status={errors.firstName && touched.firstName ? 'danger' : ''}
            onChangeText={(value) => setFieldValue('firstName', value)}
            onBlur={() => setFieldTouched('firstName')}
            testID="firstName"
            label={t('ProjectForm.first-name-label')}
            style={{ width: '48%' }}
            mandatory
          />
          <BtInput
            size="medium"
            value={values.lastName}
            status={errors.lastName && touched.lastName ? 'danger' : ''}
            onChangeText={(value) => setFieldValue('lastName', value)}
            onBlur={() => setFieldTouched('lastName')}
            testID="lastName"
            label={t('ProjectForm.last-name-label')}
            style={{ width: '48%' }}
            mandatory
          />
        </View>
        <Spacer height={44} />
        <View style={{ flexDirection: 'row-reverse' }}>
          <TouchableOpacity
            style={styles.button}
            activeOpacity={0.8}
            onPress={submitForm}
            disabled={isSubmitting}
            testID="projects-add-button"
          >
            <BtText
              style={styles.addButtonLabel}
              testID={'add-new-project-mobile-title'}
            >
              {t('ProjectForm.mobile-title')}
            </BtText>
          </TouchableOpacity>
        </View>

        <Spacer height={44} />
      </BuilderBottomModal>
    )
  },
)

const styles = StyleSheet.create({
  title1: {
    fontWeight: '700',
    fontSize: 28,
    lineHeight: 34,
    color: '#394D5A',
    marginTop: 20,
    marginBottom: 4,
  },
  title2: {
    fontWeight: '700',
    fontSize: 20,
    lineHeight: 28,
    color: '#394D5A',
    marginTop: 20,
    marginBottom: 20,
  },
  subtitle: {
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 24,
    color: '#394D5A',
    marginBottom: 20,
    letterSpacing: -0.5,
  },
  button: {
    border: 'none',
    borderRadius: 4,
    backgroundColor: '#00A0F3',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 16,
    maxWidth: 220,
    maxHeight: 48,
    marginTop: 15,
    marginBottom: 40,
  },
  addButtonLabel: {
    color: '#FFF',
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 24,
  },
})
